import type {
  AdminAuditEntry,
  AdminListResponse,
  AdminReportAnalytics,
  AdminWorkflowRun,
  MadonnaCardsResponse,
  PublicConfig,
  StatusResponse,
  WallSnapshot,
} from "../shared/types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
  }
}

const parse = async <T>(response: Response): Promise<T> => {
  const body = (await response.json()) as T & { error?: { message?: string; code?: string } };
  if (!response.ok) {
    throw new ApiError(body.error?.message ?? "Something went wrong.", response.status, body.error?.code ?? "error");
  }
  return body;
};

export const getConfig = (): Promise<PublicConfig> => fetch("/api/config").then(parse<PublicConfig>);

export const createSubmission = (
  body: FormData,
  signal?: AbortSignal,
): Promise<{ id: string; hasPhoto: boolean; statusToken: string }> =>
  fetch("/api/submissions", { method: "POST", body, signal }).then(
    parse<{ id: string; hasPhoto: boolean; statusToken: string }>,
  );

export const getStatus = (token: string): Promise<StatusResponse> =>
  fetch(`/api/status/${encodeURIComponent(token)}`).then(parse<StatusResponse>);

export const withdrawSubmission = (token: string, scope: "photo" | "all"): Promise<StatusResponse> =>
  fetch(`/api/status/${encodeURIComponent(token)}/withdraw-${scope}`, { method: "POST" }).then(parse<StatusResponse>);

export const getWall = (screenId: string): Promise<WallSnapshot> =>
  fetch(`/api/wall/${screenId}`, { cache: "no-store" }).then(parse<WallSnapshot>);

export const getMadonnaCards = (cursor?: string | null): Promise<MadonnaCardsResponse> =>
  fetch(`/api/all-cards-for-madonna${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, { cache: "no-store" })
    .then(parse<MadonnaCardsResponse>);

const adminHeaders = (): HeadersInit => {
  const localToken = localStorage.getItem("madonna-admin-token");
  return localToken ? { "x-admin-dev-token": localToken } : {};
};

export const getAdminSubmissions = (query = ""): Promise<AdminListResponse> =>
  fetch(`/api/admin/submissions${query}`, { headers: adminHeaders() }).then(parse<AdminListResponse>);

export const getAdminControl = () =>
  fetch("/api/admin/control", { headers: adminHeaders() }).then(parse<{ control: Record<string, unknown> }>);

export const adminAction = (id: string, payload: Record<string, unknown>) =>
  fetch(`/api/admin/submissions/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json", ...adminHeaders() },
    body: JSON.stringify(payload),
  }).then(parse<{ item: unknown }>);

export const deleteAdminSubmission = async (id: string): Promise<void> => {
  const response = await fetch(`/api/admin/submissions/${id}`, {
    method: "DELETE",
    headers: adminHeaders(),
  });
  if (!response.ok) await parse(response);
};

export const updateAdminControl = (payload: Record<string, unknown>) =>
  fetch("/api/admin/control", {
    method: "PATCH",
    headers: { "content-type": "application/json", ...adminHeaders() },
    body: JSON.stringify(payload),
  }).then(parse<{ control: Record<string, unknown> }>);

export const getAdminAudit = (): Promise<{ items: AdminAuditEntry[] }> =>
  fetch("/api/admin/audit", { headers: adminHeaders() }).then(parse<{ items: AdminAuditEntry[] }>);

export const getAdminWorkflows = (): Promise<{ items: AdminWorkflowRun[] }> =>
  fetch("/api/admin/workflows", { headers: adminHeaders() }).then(parse<{ items: AdminWorkflowRun[] }>);

export const getAdminReportAnalytics = (): Promise<AdminReportAnalytics> =>
  fetch("/api/admin/report-analytics", { headers: adminHeaders() }).then(parse<AdminReportAnalytics>);

export const replaceAdminImage = (id: string, image: File) => {
  const body = new FormData();
  body.set("image", image);
  return fetch(`/api/admin/submissions/${id}/replacement`, {
    method: "POST",
    headers: adminHeaders(),
    body,
  }).then(parse<{ item: unknown }>);
};

export const testAdminAlert = (): Promise<{ sent: boolean }> =>
  fetch("/api/admin/alerts/test", { method: "POST", headers: adminHeaders() }).then(parse<{ sent: boolean }>);

export const downloadAdminCsv = async (): Promise<void> => {
  const response = await fetch("/api/admin/export.csv", { headers: adminHeaders() });
  if (!response.ok) return parse(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "madonna-submissions.csv";
  link.click();
  URL.revokeObjectURL(url);
};
